/*
 * Copyright (c) 2026 Centrica.  All rights reserved.
 *
 * This source code is licensed under the license found in the
 * LICENSE file in the root directory of this source tree.
 * Date: 2026-10-07
 */
import { NebulaArchivoDescargable, NebulaImportResult, NebulaImportSource } from 'nebula-ui-kit';
import { map, Observable } from 'rxjs';
import { HttpService } from '../http/http.service';

/** Campo del multipart con el archivo, el del `/import` canonico de los servicios. */
const CAMPO_ARCHIVO = 'archivo';

/** Parametro de `ImportControlFactory` que decide si se escribe o solo se valida. */
const PARAM_PERSISTENCIA = 'persistencia';

/**
 * Origen por defecto de la importacion sobre el `/import` canonico de Simappe (HT_ARQ_013,
 * RN-TAR-079): `GET <ruta>/import/plantilla`, y `POST <ruta>/import` con `persistencia=VALIDATE`
 * para analizar o `persistencia=PERSIST` para confirmar.
 *
 * <p>Generico sobre la respuesta del servicio (`R`): cada API puede responder su propia estructura,
 * y la pantalla le dice al dialogo como leerla (orden de Carlos del 15-oct-2026).</p>
 */
export class OrigenImportacionSimappe<R = NebulaImportResult> implements NebulaImportSource<R> {
  /**
   * @param http cliente HTTP del ERP.
   * @param ruta ruta del recurso, con el prefijo del servicio (`/nebula-budget-core/api/v1/...`).
   */
  constructor(
    private readonly http: HttpService,
    private readonly ruta: string,
  ) {}

  /** @inheritdoc */
  plantilla(): Observable<NebulaArchivoDescargable> {
    return this.http
      .getBlob(`${this.ruta}/import/plantilla`)
      .pipe(map((contenido) => ({ contenido, nombre: `${this.nombreRecurso()}_plantilla.csv` })));
  }

  /** @inheritdoc */
  analizar(archivo: File, parametros?: Record<string, unknown>): Observable<R> {
    return this.enviar(archivo, { ...parametros, [PARAM_PERSISTENCIA]: 'VALIDATE' });
  }

  /** @inheritdoc */
  confirmar(archivo: File, parametros?: Record<string, unknown>): Observable<R> {
    return this.enviar(archivo, { ...parametros, [PARAM_PERSISTENCIA]: 'PERSIST' });
  }

  /**
   * Sube el archivo con los parametros como campos del multipart, igual que el dialogo anterior.
   *
   * @param archivo el archivo.
   * @param parametros parametros de control y de ambito.
   * @returns la respuesta del servicio sin el envoltorio de la plataforma.
   */
  private enviar(archivo: File, parametros: Record<string, unknown>): Observable<R> {
    const formulario = new FormData();
    formulario.append(CAMPO_ARCHIVO, archivo, archivo.name);
    Object.entries(parametros).forEach(([clave, valor]) => {
      if (valor !== undefined && valor !== null) {
        formulario.append(clave, String(valor));
      }
    });
    return this.http
      .post<unknown>(`${this.ruta}/import`, formulario)
      .pipe(map((respuesta) => desenvolver<R>(respuesta)));
  }

  /** Ultimo segmento de la ruta, para nombrar la plantilla. */
  private nombreRecurso(): string {
    return this.ruta.split('/').filter(Boolean).pop() ?? 'plantilla';
  }
}

/**
 * Quita el envoltorio de la plataforma (`response.content` o `content`) y entrega la respuesta del
 * servicio tal como llego. Como se lee lo decide la pantalla en el dialogo; sin lectura propia el
 * kit lee el `ImportResponse` estandar.
 *
 * @param respuesta lo que respondio el servicio.
 * @returns la respuesta sin envoltorio.
 */
function desenvolver<R>(respuesta: unknown): R {
  const envuelta = respuesta as { response?: { content?: R }; content?: R } | null;
  return envuelta?.response?.content ?? envuelta?.content ?? (respuesta as R);
}
